import {
  Component,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnInit,
  Output
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormValidation } from '@core/services/form-validation';

export interface DropdownOption {
  value: string | number;
  label: string;
}

@Component({
  selector: 'app-input-multi-select',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './input-multi-select.html',
  styleUrls: ['./input-multi-select.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputMultiSelect),
      multi: true,
    },
  ],
})
export class InputMultiSelect implements ControlValueAccessor, OnInit {
  // 🔥 static tracker for open dropdown
  private static openDropdown: InputMultiSelect | null = null;

  @Input() label: string = '';
  @Input() placeholder: string = 'Select options';
  @Input() options: DropdownOption[] = [];
  @Input() formControlName: string = '';
  @Input() control: AbstractControl | null = new FormControl();
  @Input() showSearch: boolean = false;
  @Input() title: string = '';
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Output() change = new EventEmitter<(string | number)[]>();
  isOpen = false;
  values: (string | number)[] = [];
  searchTerm = '';
  filteredOptions: DropdownOption[] = [];

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(public formValidation: FormValidation) {}

  ngOnInit(): void {
    this.filteredOptions = [...this.options];
  }

  toggleDropdown(event: Event) {
    event.stopPropagation();
    if (this.control?.disabled) return;

    if (this.isOpen) {
      this.isOpen = false;
      InputMultiSelect.openDropdown = null;
    } else {
      if (InputMultiSelect.openDropdown && InputMultiSelect.openDropdown !== this) {
        InputMultiSelect.openDropdown.isOpen = false;
      }
      InputMultiSelect.openDropdown = this;
      this.isOpen = true;
      this.searchTerm = '';
      this.filteredOptions = [...this.options];
    }

    if (this.isOpen) this.onTouched();
  }

  toggleOption(option: DropdownOption, event?: Event) {
    event?.stopPropagation(); // ✅ prevent bubbling up
    const idx = this.values.indexOf(option.value);
    if (idx > -1) {
      this.values.splice(idx, 1);
    } else {
      this.values.push(option.value);
    }
    this.onChange(this.values);
    this.control?.markAsTouched();
    this.control?.markAsDirty();
    this.control?.updateValueAndValidity();
    // Emit change event with current values array
    this.change.emit([...this.values]);
  }

  isSelected(value: string | number): boolean {
    return this.values.includes(value);
  }

  filterOptions() {
    const term = this.searchTerm.toLowerCase();
    this.filteredOptions = this.options.filter(opt =>
      opt.label.toLowerCase().includes(term)
    );
  }

  writeValue(value: any): void {
    this.values = value || [];
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  get selectedLabels(): string {
    return this.options
      .filter(opt => this.values.includes(opt.value))
      .map(opt => opt.label)
      .join(', ');
  }

  get errorMessage(): string | null {
    if (this.control?.touched && this.control?.invalid) {
      return this.formValidation.getValidationMessage(this.control, this.formControlName);
    }
    return null;
  }

  get required(): boolean {
    if (!this.control || !this.control.validator) return false;
    const validator = this.control.validator({} as AbstractControl);
    return !!validator?.['required'];
  }

  @HostListener('document:click', ['$event'])
  closeDropdownOnOutsideClick(event: MouseEvent) {
    if (!(event.target as HTMLElement).closest('.custom-multi-select')) {
      this.isOpen = false;
      if (InputMultiSelect.openDropdown === this) {
        InputMultiSelect.openDropdown = null;
      }
    }
  }
}
